import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios, { isAxiosError } from 'axios';
import { useNavigate, useParams } from 'react-router';
import { useDispatch } from 'react-redux';
import { Link2Off } from 'lucide-react';
import config from 'constants/apiConfig';
import {
  updateCurrentIncident,
  updateCurrentlySelectedGroup,
} from 'actions/diceActions';
import {
  selectGroupGuid,
  selectIncidents,
  useAppSelector,
} from 'slices/commonSelectors';
import { Button } from 'components/ui/button';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from 'components/ui/empty';

type ResolvedLink = { path: string; groupGuid: string; isMember: boolean };

type Group = { group_guid: string };

function failureMessage(error: unknown): {
  title: string;
  description: string;
} {
  const status = isAxiosError(error) ? error.response?.status : undefined;
  if (status === 410) {
    return {
      title: 'This link has expired',
      description:
        'Notification links work for 30 days. Open PRATUS to find the item from your dashboard.',
    };
  }
  if (status === 404) {
    return {
      title: 'This link is not valid',
      description: 'Check that the whole link was copied from the message.',
    };
  }
  return {
    title: 'This link could not be opened',
    description: 'Try again in a moment.',
  };
}

/**
 * Opens the item a notification's short link points to, switching to its
 * organization first.
 */
export function ShortLinkRedirect() {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentGroupGuid = useAppSelector(selectGroupGuid);
  const groups = useAppSelector((state) => state.app.groups as Group[]);
  const areGroupsLoaded = useAppSelector(
    (state) => state.app.areGroupsLoaded as boolean
  );
  const incidents = useAppSelector(selectIncidents);
  const redirected = useRef(false);

  const link = useQuery({
    queryKey: ['notification-link', token],
    queryFn: async () => {
      const resp = await axios.get<ResolvedLink>(
        `${config.apiGateway.notification_links}/${encodeURIComponent(token)}`
      );
      return resp.data;
    },
    enabled: !!token,
    retry: false,
    staleTime: Infinity,
  });

  const resolved = link.data;
  const targetGroup = resolved
    ? groups?.find((group) => group.group_guid === resolved.groupGuid)
    : undefined;
  const notMember =
    !!resolved && (!resolved.isMember || (areGroupsLoaded && !targetGroup));

  useEffect(() => {
    if (!resolved || notMember || redirected.current) return;
    if (resolved.groupGuid !== currentGroupGuid) {
      if (!targetGroup) return;
      dispatch(updateCurrentlySelectedGroup(targetGroup) as never);
      const firstIncident = incidents.find(
        (incident: { group_guid?: string }) =>
          incident.group_guid === resolved.groupGuid
      );
      if (firstIncident)
        dispatch(updateCurrentIncident(firstIncident) as never);
    }
    redirected.current = true;
    navigate(resolved.path, { replace: true });
  }, [
    resolved,
    notMember,
    currentGroupGuid,
    targetGroup,
    incidents,
    dispatch,
    navigate,
  ]);

  if (link.isError || notMember) {
    const message = notMember
      ? {
          title: 'This link is for another organization',
          description:
            'You are not a member of the organization that sent it. Ask an admin there to add you, then open the link again.',
        }
      : failureMessage(link.error);
    return (
      <Empty className="tw:min-h-[60vh]">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Link2Off />
          </EmptyMedia>
          <EmptyTitle>{message.title}</EmptyTitle>
          <EmptyDescription>{message.description}</EmptyDescription>
        </EmptyHeader>
        <Button
          variant="outline"
          onClick={() => navigate('/hub', { replace: true })}
        >
          Go to dashboard
        </Button>
      </Empty>
    );
  }

  return (
    <Empty className="tw:min-h-[60vh]">
      <EmptyHeader>
        <EmptyTitle>Opening link…</EmptyTitle>
      </EmptyHeader>
    </Empty>
  );
}

export default ShortLinkRedirect;
